import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, ArrowRight, Shield, Globe } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const { language, toggleLanguage } = useLanguage();
  const isAr = language === 'ar';
  const Back = isAr ? ArrowRight : ArrowLeft;

  const content = {
    ar: {
      title: 'سياسة الخصوصية',
      lastUpdated: 'آخر تحديث: أبريل 2026',
      backToHome: 'الرئيسية',
      sections: [
        {
          heading: '1. مقدمة',
          text: 'مرحباً بكم في B2B Market Iraq. نحن نقدر خصوصيتكم ونلتزم بحماية بياناتكم الشخصية. توضح سياسة الخصوصية هذه كيفية جمع واستخدام وحماية معلوماتكم عند استخدام منصتنا.'
        },
        {
          heading: '2. البيانات التي نجمعها',
          text: 'نقوم بجمع المعلومات التالية:',
          list: [
            'معلومات الحساب: الاسم، البريد الإلكتروني، كلمة المرور (مشفرة)',
            'معلومات الشركة: اسم الشركة، الوصف، نوع الحساب (بائع/مشتري)',
            'معلومات الاتصال: رقم الهاتف، العنوان، الدولة',
            'صور البروفايل والصور المرفقة بالعروض والطلبات',
            'بيانات الاستخدام: سجل الدخول، المشاهدات، الرسائل'
          ]
        },
        {
          heading: '3. كيف نستخدم بياناتكم',
          text: 'نستخدم بياناتكم للأغراض التالية:',
          list: [
            'إنشاء وإدارة حسابكم على المنصة',
            'عرض عروضكم وطلباتكم في السوق',
            'التواصل معكم بشأن حالة حسابكم وعروضكم',
            'إرسال إشعارات مهمة عبر البريد الإلكتروني والرسائل النصية',
            'التحقق من رقم هاتفكم عبر رسائل واتساب (OTP)',
            'تحسين خدماتنا وتحليل استخدام المنصة',
            'ضمان أمان المنصة ومنع الاحتيال'
          ]
        },
        {
          heading: '4. مشاركة البيانات مع أطراف ثالثة',
          text: 'نحن لا نبيع بياناتكم الشخصية. قد نشارك بعض المعلومات مع:',
          list: [
            'Twilio: لإرسال رسائل التحقق عبر واتساب ورسائل SMS الإعلانية',
            'Resend: لإرسال إشعارات البريد الإلكتروني',
            'الشركات الأخرى على المنصة: يتم عرض اسم شركتكم ومعلومات الاتصال للمستخدمين المسجلين فقط',
            'الجهات القانونية: عند الطلب بموجب القانون'
          ]
        },
        {
          heading: '5. حماية البيانات',
          text: 'نتخذ إجراءات أمنية لحماية بياناتكم:',
          list: [
            'تشفير كلمات المرور باستخدام bcrypt',
            'استخدام HTTPS لجميع الاتصالات',
            'JWT tokens للمصادقة الآمنة',
            'تحديد عدد محاولات إرسال OTP لمنع إساءة الاستخدام'
          ]
        },
        {
          heading: '6. حقوقكم',
          text: 'لديكم الحقوق التالية:',
          list: [
            'الوصول إلى بياناتكم الشخصية وتعديلها',
            'طلب حذف حسابكم وبياناتكم',
            'إلغاء الاشتراك في الرسائل الإعلانية',
            'طلب نسخة من بياناتكم المخزنة'
          ]
        },
        {
          heading: '7. ملفات تعريف الارتباط (Cookies)',
          text: 'نستخدم التخزين المحلي (localStorage) لحفظ رمز الدخول الخاص بكم وتفضيلات اللغة. لا نستخدم ملفات تعريف الارتباط لأغراض التتبع أو الإعلانات.'
        },
        {
          heading: '8. التعديلات على السياسة',
          text: 'قد نقوم بتحديث سياسة الخصوصية من وقت لآخر. سنخطركم بأي تغييرات جوهرية عبر البريد الإلكتروني أو إشعار على المنصة.'
        },
        {
          heading: '9. التواصل معنا',
          text: 'إذا كانت لديكم أي أسئلة أو استفسارات حول سياسة الخصوصية، يرجى التواصل معنا:',
          list: [
            'البريد الإلكتروني: info@b2bmarketiraq.com',
            'عبر نظام الرسائل في المنصة'
          ]
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: April 2026',
      backToHome: 'Home',
      sections: [
        {
          heading: '1. Introduction',
          text: 'Welcome to B2B Market Iraq. We value your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and protect your information when using our platform.'
        },
        {
          heading: '2. Data We Collect',
          text: 'We collect the following information:',
          list: [
            'Account information: Name, email address, password (encrypted)',
            'Company information: Company name, description, account type (seller/buyer)',
            'Contact information: Phone number, address, country',
            'Profile pictures and images attached to offers and requests',
            'Usage data: Login history, views, messages'
          ]
        },
        {
          heading: '3. How We Use Your Data',
          text: 'We use your data for the following purposes:',
          list: [
            'Creating and managing your account on the platform',
            'Displaying your offers and requests in the marketplace',
            'Communicating with you about your account and offer status',
            'Sending important notifications via email and SMS',
            'Verifying your phone number via WhatsApp OTP',
            'Improving our services and analyzing platform usage',
            'Ensuring platform security and preventing fraud'
          ]
        },
        {
          heading: '4. Sharing Data with Third Parties',
          text: 'We do not sell your personal data. We may share some information with:',
          list: [
            'Twilio: For sending WhatsApp verification messages and promotional SMS',
            'Resend: For sending email notifications',
            'Other companies on the platform: Your company name and contact info are shown to registered users only',
            'Legal authorities: When required by law'
          ]
        },
        {
          heading: '5. Data Protection',
          text: 'We take security measures to protect your data:',
          list: [
            'Password encryption using bcrypt',
            'HTTPS for all communications',
            'JWT tokens for secure authentication',
            'Rate limiting on OTP requests to prevent abuse'
          ]
        },
        {
          heading: '6. Your Rights',
          text: 'You have the following rights:',
          list: [
            'Access and modify your personal data',
            'Request deletion of your account and data',
            'Opt out of promotional messages',
            'Request a copy of your stored data'
          ]
        },
        {
          heading: '7. Cookies',
          text: 'We use localStorage to save your access token and language preferences. We do not use cookies for tracking or advertising purposes.'
        },
        {
          heading: '8. Policy Updates',
          text: 'We may update this Privacy Policy from time to time. We will notify you of any significant changes via email or a platform notification.'
        },
        {
          heading: '9. Contact Us',
          text: 'If you have any questions about this Privacy Policy, please contact us:',
          list: [
            'Email: info@b2bmarketiraq.com',
            'Via the messaging system on the platform'
          ]
        }
      ]
    }
  };

  const c = content[isAr ? 'ar' : 'en'];

  return (
    <div className="min-h-screen bg-background" dir={isAr ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors text-sm font-medium">
            <Back size={18} />
            {c.backToHome}
          </Link>
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            <Globe size={16} />
            {isAr ? 'English' : 'عربي'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-3 mb-2">
          <Shield size={28} className="text-accent" />
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-primary">{c.title}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8 sm:mb-10">{c.lastUpdated}</p>

        <div className="space-y-8">
          {c.sections.map((section, idx) => (
            <div key={section.heading} className="bg-white rounded-xl border border-border p-5 sm:p-6">
              <h2 className="text-lg sm:text-xl font-heading font-bold text-primary mb-3">{section.heading}</h2>
              <p className="text-sm sm:text-base text-foreground leading-relaxed">{section.text}</p>
              {section.list && (
                <ul className={`mt-3 space-y-2 ${isAr ? 'pr-5' : 'pl-5'}`}>
                  {section.list.map((item, i) => (
                    <li key={item} className="text-sm sm:text-base text-foreground leading-relaxed list-disc">{item}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-border text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} B2B Market Iraq. {isAr ? 'جميع الحقوق محفوظة.' : 'All rights reserved.'}
          </p>
        </div>
      </div>
    </div>
  );
}
